import React, { useState } from 'react';
import cn from '../../lib/cn';
import site from '../../data/site';
import { showroom, ambienceClip } from '../../data/media';
import { Section, SectionHeading } from '../ui/Section';
import Reveal from '../ui/Reveal';
import Button from '../ui/Button';
import Icon from '../ui/Icon';
import Lightbox from '../ui/Lightbox';
import AmbientVideo from '../ui/AmbientVideo';

/**
 * The showroom itself, as a mosaic. Wide shots take two columns so the grid
 * reads as a considered layout rather than a uniform thumbnail wall.
 */
export function Showroom() {
  const [lightbox, setLightbox] = useState(null);
  const clip = ambienceClip('ambience-03');

  return (
    <Section id="showroom" tone="canvas">
      <div className="shell">
        <SectionHeading
          eyebrow="Inside the showroom"
          title={['6-G/103, C G Road', '— come and sit down']}
          lede="Opposite Lal Bunglow in Ahmedabad. Air-conditioned counters, seating for the whole family, and a shrine at the centre of the floor."
          action={
            <Button href={site.address.mapsHref} variant="outline" size="md">
              <Icon name="pin" size={16} />
              Open in Maps
            </Button>
          }
        />

        <div className="mt-12 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
          {showroom.map((shot, i) => (
            <Reveal
              key={shot.slug}
              variant="scale"
              duration={860}
              delay={(i % 4) * 80}
              className={cn(shot.span === 2 && 'col-span-2')}
            >
              <button
                type="button"
                onClick={() => setLightbox(i)}
                aria-label={`View: ${shot.alt}`}
                className="group relative block w-full overflow-hidden rounded-sm bg-canvas-soft"
              >
                <img
                  src={shot.thumb}
                  alt={shot.alt}
                  loading="lazy"
                  decoding="async"
                  className={cn(
                    'w-full object-cover transition-transform duration-[900ms] ease-silk group-hover:scale-105',
                    shot.span === 2 ? 'aspect-[16/10]' : 'aspect-[4/5] sm:aspect-square'
                  )}
                />
                <span className="pointer-events-none absolute inset-0 bg-ink-950/0 transition-colors duration-500 group-hover:bg-ink-950/25" />
                <span className="pointer-events-none absolute bottom-3 right-3 grid h-9 w-9 scale-75 place-items-center rounded-full bg-canvas text-ink-950 opacity-0 transition-all duration-400 ease-silk group-hover:scale-100 group-hover:opacity-100">
                  <Icon name="arrowUpRight" size={16} />
                </span>
              </button>
            </Reveal>
          ))}
        </div>

        {/* Ambience strip: a clip of the floor plus the visit essentials. */}
        <Reveal delay={100} variant="up" distance={30} duration={950} className="mt-14">
          <div className="grid overflow-hidden rounded-sm bg-ink-950 sm:grid-cols-[auto_1fr]">
            <AmbientVideo
              src={clip.src}
              poster={clip.poster}
              alt={clip.alt}
              className="h-56 w-full sm:h-full sm:w-56"
            />
            <div className="flex flex-col justify-center gap-6 p-8 sm:p-10">
              <p className="max-w-lg font-display text-2xl leading-snug text-canvas sm:text-3xl">
                Trays come out, chairs pull in, and nothing is rushed.
              </p>
              <div className="flex flex-wrap gap-x-10 gap-y-4">
                <div>
                  <p className="eyebrow mb-1.5 text-champagne-300">Address</p>
                  <p className="text-sm leading-relaxed text-canvas/65">
                    {site.address.line1}, {site.address.line2}
                    <br />
                    {site.address.city} {site.address.postalCode}
                  </p>
                </div>
                <div>
                  <p className="eyebrow mb-1.5 text-champagne-300">Call ahead</p>
                  <a
                    href={site.contact.phoneHref}
                    className="text-sm tabular-nums text-canvas/65 transition hover:text-champagne-300"
                  >
                    {site.contact.phoneDisplay}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>

      <Lightbox
        items={showroom}
        index={lightbox}
        onClose={() => setLightbox(null)}
        onNavigate={setLightbox}
      />
    </Section>
  );
}

export default Showroom;
